import { ApiService } from '../apiService'
import apiClient from '../client'
import type {
  ConversationDto,
  ChatMessageDto,
  MessagesResponse,
  SendMessageRequest,
  CreateDirectConversationRequest,
  CreateGroupConversationRequest,
  ChatUserSearchResult,
  ChatNotification,
  OnlineFriend,
  ChatImageUploadResponse,
  ImageViewResponse,
  ConversationSettings,
} from '../types/chat'
import { notificationModeToInt } from '../types/chat'

export const chatApi = {
  // Get all conversations for the current user
  getConversations: () =>
    ApiService.get<ConversationDto[]>('/api/Chat/conversations'),

  // Get a specific conversation by ID
  getConversation: (conversationId: string) =>
    ApiService.get<ConversationDto>(`/api/Chat/conversations/${conversationId}`),

  // Get paginated messages for a conversation
  getMessages: (conversationId: string, page: number = 1, pageSize: number = 50) =>
    ApiService.get<ChatMessageDto[]>(
      `/api/Chat/conversations/${conversationId}/messages?page=${page}&pageSize=${pageSize}`
    ),

  // Send a message to a conversation
  sendMessage: (conversationId: string, request: SendMessageRequest) =>
    ApiService.post<ChatMessageDto>(
      `/api/Chat/conversations/${conversationId}/messages`,
      request
    ),

  // Create a direct conversation with another user
  createDirectConversation: (request: CreateDirectConversationRequest) =>
    ApiService.post<ConversationDto>('/api/Chat/conversations/direct', request),

  // Create a group conversation
  createGroupConversation: (request: CreateGroupConversationRequest) =>
    ApiService.post<ConversationDto>('/api/Chat/conversations/group', request),

  // Mark a conversation as read
  markAsRead: (conversationId: string) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/read`, {}),

  // Search for users to start a chat with
  searchUsers: (query: string) =>
    ApiService.get<ChatUserSearchResult[]>(`/api/Chat/users/search?q=${encodeURIComponent(query)}`),

  // Get online friends
  getOnlineFriends: () =>
    ApiService.get<OnlineFriend[]>('/api/Chat/friends/online'),

  // Get chat notifications
  getNotifications: () =>
    ApiService.get<ChatNotification[]>('/api/Chat/notifications'),

  // Get unread message count
  getUnreadCount: () =>
    ApiService.get<number>('/api/ChatNotification/unread-count'),

  // Mark all notifications as read
  markAllNotificationsRead: () =>
    ApiService.post<void>('/api/ChatNotification/mark-all-read', {}),

  // Delete a message
  deleteMessage: (conversationId: string, messageId: string) =>
    ApiService.delete<void>(`/api/Chat/conversations/${conversationId}/messages/${messageId}`),

  // Edit a message
  editMessage: (conversationId: string, messageId: string, content: string) =>
    ApiService.put<ChatMessageDto>(
      `/api/Chat/conversations/${conversationId}/messages/${messageId}`,
      { content }
    ),

  // Leave a conversation (group or adventure)
  leaveConversation: (conversationId: string) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/leave`, {}),

  // Mute a conversation
  muteConversation: (conversationId: string, muted: boolean) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/mute`, { muted }),

  // Pin a conversation
  pinConversation: (conversationId: string, pinned: boolean) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/pin`, { pinned }),

  // Add participants to a group conversation
  addParticipants: (conversationId: string, participantIds: string[]) =>
    ApiService.post<void>(`/api/Chat/conversations/${conversationId}/participants`, { participantIds }),

  // Remove a participant from a group conversation
  removeParticipant: (conversationId: string, participantId: string) =>
    ApiService.delete<void>(`/api/Chat/conversations/${conversationId}/participants/${participantId}`),

  // Upload an image for chat messages
  uploadImage: async (file: File): Promise<ChatImageUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ChatImageUploadResponse>('/api/Chat/images', formData)
  },

  // Attach an existing media-library image to chat (server copies it into a chat-owned image)
  attachLibraryImage: (fileId: string) =>
    ApiService.post<ChatImageUploadResponse>('/api/Chat/images/from-library', { fileId }),

  // Open a message's image. A view-limited one spends a view (the sender's own preview is free) and
  // returns a one-time viewToken; an ordinary one returns its imageUrl. 403 when out of views.
  viewImage: (messageId: string) =>
    ApiService.post<ImageViewResponse>(`/api/Chat/messages/${messageId}/view`, {}),

  // The bytes of a view-limited image for a one-time token (consumed by this request: 410 on reuse).
  fetchImageByToken: async (token: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`/api/Chat/images/${encodeURIComponent(token)}`, {
      responseType: 'blob',
    })
    return response.data
  },

  // Hide a message from the caller's own chat history (not a delete)
  hideMessage: (messageId: string) =>
    ApiService.post<void>(`/api/Chat/messages/${messageId}/hide`, {}),

  // Update conversation settings
  updateConversationSettings: (conversationId: string, settings: ConversationSettings) =>
    ApiService.put<ConversationSettings>(
      `/api/Chat/conversations/${conversationId}/settings`,
      { notificationMode: notificationModeToInt(settings.notificationMode) }
    ),

  // Add or remove a reaction on a message
  reactToMessage: (conversationId: string, messageId: string, emoji: string | null) =>
    ApiService.post<void>(
      `/api/Chat/conversations/${conversationId}/messages/${messageId}/reaction`,
      { emoji: emoji || null }
    ),

  // Get or join a group chat conversation
  getOrJoinGroupChat: (groupId: string) =>
    ApiService.post<ConversationDto>(`/api/Chat/conversations/group/${groupId}/join`, {}),

  // The adventure's editor chat: created on first use, editors only. 404 when the caller may not edit.
  openEditorChat: (adventureId: string) =>
    ApiService.post<ConversationDto>(`/api/Chat/conversations/adventure/${adventureId}/editor`, {}),

  // Get or join an adventure chat conversation
  getOrJoinAdventureChat: (adventureId: string) =>
    ApiService.post<ConversationDto>(`/api/Chat/conversations/adventure/${adventureId}/join`, {}),
}
